import React, { useEffect } from "react";
import { OnlineMerchantManagement } from "components/OnlineMerchant/OnlineMerchantManagement";
import { useForm, useWatch } from "react-hook-form";
import { Stack, Step, StepLabel, Stepper } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { FormBuilder } from "components/FormBuilder/FormBuilder";
import { queryDropdownOptions } from "./model";
import Divider from "components/Divider/Divider";
import { useTranslation } from "react-i18next";
import useSnackbarMAS from "utils/useSnackbarMAS/useSnackbarMAS";
import merchantManagement from "routes/merchantManagement";
import { formPreventEnterSubmit } from "utils/utils";
import usePageWrapper from "utils/usePageWrapper/usePageWrapper";

const AddOnlineMerchant = ({ path, redirectFn }) => {
  const { t: tLocal } = useTranslation("translation", { keyPrefix: 'page.merchantManagement.addOnlineMerchant' });
  const { t: tCommon } = useTranslation();
  const { snackbarError, snackbarSuccess } = useSnackbarMAS();
  const formProps = useForm({
    mode: "onChange",
  });
  const { handleSubmit } = formProps;
  const { handleApi, PageWrapper, PageOuterContainer, PageStickyHeader, PageStickyFooter } = usePageWrapper();

  const allowUsdTransacton = !!(useWatch({ control: formProps.control, name: "currencyList" })?.includes("USD"));

  const [isLoading, setIsLoading] = React.useState(true);
  const [dropdownOptions, setDropdownOptions] = React.useState({});
  const [step, setStep] = React.useState(0);

  const steps = [
    {
      label: tLocal("stepper.label.info"),
      content: <>
        <OnlineMerchantManagement.MerchantForm.BasicInfoSection
          formProps={formProps}
          isCreate
          isLoading={isLoading}
          mccDropdownOptions={(dropdownOptions?.mccList ?? []).map((mccObj) => ({
            key: mccObj.key,
            title: mccObj.title,
            value: mccObj.key,
          }))} />
        <Divider />
        <OnlineMerchantManagement.MerchantForm.CheckoutConfigSection
          formProps={formProps}
          isCreate
          isLoading={isLoading}
        />
        <Divider />
        <OnlineMerchantManagement.MerchantForm.OtherSection
          formProps={formProps}
          isCreate
          isLoading={isLoading}
          salesOptions={(dropdownOptions?.salesList ?? []).map((salesObj) => ({
            key: salesObj.id,
            title: salesObj.firstName,
            value: salesObj.id,
          }))}
          referrerOptions={(dropdownOptions?.referrerList ?? []).map((referrerObj) => ({
            key: referrerObj.id,
            title: referrerObj.company,
            value: referrerObj.id,
          }))}
        />
        <Divider />
        <OnlineMerchantManagement.MerchantForm.BankSection
          formProps={formProps}
          isCreate
          isLoading={isLoading}
        />
        <Divider />
        <OnlineMerchantManagement.MerchantForm.FinanceSection
          formProps={formProps}
          isCreate
          isLoading={isLoading}
        />
        <Divider />
        <OnlineMerchantManagement.MerchantForm.SettlementSection
          formProps={formProps}
          isCreate
          isLoading={isLoading}
        />
        {
          allowUsdTransacton &&
          <>
            <Divider />
            <OnlineMerchantManagement.MerchantForm.UsdSettlementSection
              formProps={formProps}
              isCreate
              isLoading={isLoading}
            />
          </>
        }
      </>
    },
    {
      label: tLocal("stepper.label.processorMdr"),
      content: <>
        <OnlineMerchantManagement.MerchantForm.ProcessorEnableSection
          formProps={formProps}
          isCreate
          isLoading={isLoading}
        />
        <Divider />
        <OnlineMerchantManagement.MerchantForm.PaymentConfigSection
          formProps={formProps}
          isCreate
          isLoading={isLoading}
        />
        <Divider />
        <OnlineMerchantManagement.MerchantForm.MdrSection
          formProps={formProps}
          isCreate
          isLoading={isLoading}
        />
      </>
    },
  ]

  useEffect(() => {
    const fetchOptions = async () => {
      setIsLoading(true);
      const resp = await queryDropdownOptions();
      if (resp?.data?.responseCode === "0000") {
        const data = resp.data.data;
        setDropdownOptions({
          serviceIntegratorList: data?.serviceIntegratorList,
          referrerList: data?.referrerList,
          salesList: data?.salesList,
          mccList: data?.mccList,
        });
      } else {
        console.error("Failed to fetch dropdown options");
      }
      setIsLoading(false);
    }

    fetchOptions();
  }, []);

  const onSubmit = async (formValues) => {
    const hasNuveiSiteId = !!formValues.siteId;

    // const test = true;
    // if (test) {
    //   console.log("create merchant payload", OnlineMerchantManagement.PayloadBuilder.createMerchantEntity(formValues));
    //   hasNuveiSiteId && console.log("update merchant nuvei config payload", OnlineMerchantManagement.PayloadBuilder.updateNuveiConfig({ merchantId: 1, ...formValues }));
    //   console.log("create merchant mdr payload", OnlineMerchantManagement.PayloadBuilder.createMerchantMdr({ merchantId: 1, ...formValues }));
    // }

    setIsLoading(true);

    const createEntityResult = await OnlineMerchantManagement.Api.createMerchantEntity(OnlineMerchantManagement.PayloadBuilder.createMerchantEntity(formValues));
    const merchantId = createEntityResult?.data?.merchId;
    if (!handleApi(createEntityResult)) {
      snackbarError({ message: tLocal("toast.createEntity.failed") });
    } else {

      const [updateProcessorConfigResult, createMdrResult] = await Promise.all([
        hasNuveiSiteId && OnlineMerchantManagement.Api.updateMerchantProcessorConfig(OnlineMerchantManagement.PayloadBuilder.updateNuveiConfig({ merchantId: merchantId, ...formValues })),
        OnlineMerchantManagement.Api.createMerchantMdr(OnlineMerchantManagement.PayloadBuilder.createMerchantMdr({ merchantId: merchantId, ...formValues })),
      ])

      let isAllApiCallSuccess = true;

      if (!handleApi(updateProcessorConfigResult)) {
        snackbarError({ message: tLocal("toast.createNuveiConfig.failed") });
        isAllApiCallSuccess = false;
      };

      if (!handleApi(createMdrResult)) {
        snackbarError({ message: tLocal("toast.createMdr.failed") });
        isAllApiCallSuccess = false;
      }

      if (isAllApiCallSuccess) {
        snackbarSuccess({ message: tLocal("toast.create.success") });
      }

      // redirect to created merchant detail page
      const redirectPath = "/merchant-management/merchants/online-merchant-detail";
      const payload = {
        name:
          merchantManagement
            .find((tab) => tab.tabName === "Merchants")
            .page.find((page) => page.path === redirectPath).name + merchantId,
        path: redirectPath,
        state: {
          merchantId: merchantId,
        },
      };
      redirectFn("close", { tabId: path })
      redirectFn("redirectPage", payload);
    }

    setIsLoading(false);
  }

  return (
    <PageOuterContainer>
      <PageWrapper noPadding isLoading={isLoading}>
        <form noValidate onSubmit={handleSubmit(onSubmit)} onKeyDown={formPreventEnterSubmit}>
          <PageStickyHeader>
            <Stepper
              activeStep={step}
              alternativeLabel
              sx={{
                "& .MuiStepIcon-root.Mui-active": { color: "rgb(54, 180, 191)" },
                "& .MuiStepIcon-root.Mui-completed": { color: "rgb(54, 180, 191)" },
              }}
            >
              {steps.map((s) => (
                <Step key={s.label}>
                  <StepLabel>{s.label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </PageStickyHeader>
          <FormBuilder.Section>
            {
              steps[step].content
            }
          </FormBuilder.Section>
          <PageStickyFooter>
            <Stack
              direction="row"
              justifyContent="space-between"
              gap={2}
            >
              {step > 0 ?
                <LoadingButton
                  key="back"
                  variant="outline-normal-button"
                  type="button"
                  onClick={() => setStep((prev) => Math.max(prev - 1, 0))}
                >
                  {tCommon("common.previous")}
                </LoadingButton> : <div></div>
              }
              {
                step < steps.length - 1 ?
                  (
                    <LoadingButton
                      key="next"
                      variant="normal-button"
                      type="button"
                      onClick={async () => {
                        await formProps?.trigger(undefined, { shouldFocus: true }) &&
                          setStep((prev) => Math.min(prev + 1, steps.length - 1))
                      }}
                    >
                      {tCommon("common.next")}
                    </LoadingButton>
                  ) : (
                    <LoadingButton
                      key="submit"
                      variant="normal-button"
                      type="submit"
                      loading={isLoading}
                    >
                      {tCommon("common.submit")}
                    </LoadingButton>
                  )
              }
            </Stack>
          </PageStickyFooter>
        </form>
      </PageWrapper>
    </PageOuterContainer>
  )
};

export default React.memo(AddOnlineMerchant);
